import { escritsData, type EscritEntry } from "../data/escrits";
import { Button } from "./ui/button";
import {
    getDaysInYear,
    getDateFromDayOfYear,
    formatDate,
    getDayName,
    isPast,
    isToday,
} from "../lib/calendar";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "./ui/tooltip";
import { useEffect } from "react";

const YEAR = 2026;
const TEMPLATE_URL = "https://docs.google.com/document/d/120YciDdvKAOKdoZ2MA0xIsQWylsp2GnQnyd5x-5VV-A/edit?tab=t.0";

interface YearGridProps {
    year: number;
    entries: EscritEntry[];
}

function YearGrid({ year, entries }: YearGridProps) {
    const totalDays = getDaysInYear(year);
    const specialEntries = entries.filter(e => e.label);
    
    // Create a map of dates to URLs for quick lookup
    const dateMap = new Map<string, string>();
    entries.filter(e => e.date).forEach(entry => {
        if (entry.date) {
            dateMap.set(entry.date, entry.url);
        }
    });

    const entriesCount = entries.filter(e => e.date || e.label).length;

    return (
        <div className="mb-20 p-6 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/20 transition-all">
            {/* Year header with stats */}
            <div className="flex items-baseline justify-between mb-6">
                <h2 className="text-4xl font-bold text-white">{year}</h2>
                <div className="text-sm text-gray-400">
                    <span className="font-semibold text-red-400">{entriesCount}</span> {entriesCount === 1 ? 'escrit' : 'escrits'}
                </div>
            </div>

            {/* All days grid */}
            <div className="grid grid-cols-23 gap-2 mb-6 mx-auto" style={{ maxWidth: '600px' }}>
                {Array.from({ length: totalDays }, (_, i) => {
                    const dayOfYear = i + 1;
                    const date = getDateFromDayOfYear(year, dayOfYear);
                    const formattedDate = formatDate(date);
                    const url = dateMap.get(formattedDate);
                    const hasPassed = isPast(dayOfYear, year);
                    const isCurrentDay = year === YEAR && isToday(dayOfYear, year);
                    
                    return (
                        <DayDot
                            key={dayOfYear}
                            date={date}
                            url={url}
                            hasPassed={hasPassed}
                            isCurrentDay={isCurrentDay}
                        />
                    );
                })}
            </div>

            {/* Special entry buttons */}
            {specialEntries.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-6 justify-center">
                    {specialEntries.map((entry, idx) => (
                        <Button
                            key={idx}
                            onClick={() => window.open(entry.url, '_blank')}
                            className="bg-linear-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white border-none shadow-md hover:shadow-lg text-sm font-medium h-auto py-2 px-5 transition-all"
                        >
                            {entry.label}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
}

interface DayDotProps {
    date: Date;
    url?: string;
    hasPassed: boolean;
    isCurrentDay?: boolean;
}

function DayDot({ date, url, hasPassed, isCurrentDay }: DayDotProps) {
    const hasEntry = !!url;
    
    const handleClick = () => {
        if (hasEntry) {
            window.open(url, '_blank');
        } else {
            // Open template for new document
            window.open(TEMPLATE_URL, '_blank');
        }
    };

    // Determine dot style based on state
    let dotClass = "w-3 h-3 rounded-full transition-all duration-200 cursor-pointer p-0 min-w-0";

    if (isCurrentDay) {
        dotClass += " bg-red-600 hover:bg-red-500 hover:scale-125 border-none";
    } else if (hasEntry) {
        dotClass += " bg-red-600 hover:bg-red-500 hover:scale-125 border-none";
    } else if (hasPassed) {
        dotClass += " bg-white hover:bg-gray-200 hover:scale-125 border-none";
    } else {
        dotClass += " bg-gray-600 hover:bg-gray-500 hover:scale-125 border-none";
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    onClick={handleClick}
                    className={dotClass}
                    aria-label={`${getDayName(date)}, ${formatDate(date)}`}
                />
            </TooltipTrigger>
            <TooltipContent className="bg-white text-black border-none shadow-lg px-3 py-2">
                <div className="text-xs font-semibold text-gray-900">{getDayName(date)}</div>
                <div className="text-sm font-medium">{formatDate(date)}</div>
            </TooltipContent>
        </Tooltip>
    );
}

export function Escrits() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const years = [2026, 2025, 2024, 2023, 2022];
    
    const totalEscrits = escritsData.filter(e => e.date || e.label).length;

    const handleRandomDay = () => {
        const datedEntries = escritsData.filter(e => e.date);
        if (datedEntries.length > 0) {
            const randomEntry = datedEntries[Math.floor(Math.random() * datedEntries.length)];
            window.open(randomEntry.url, '_blank');
        }
    };

    return (
        <TooltipProvider>
            <div className="min-h-screen bg-black text-white p-8">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="mb-12">
                        <div className="flex justify-between items-start mb-4">
                            <h1 className="text-5xl font-bold bg-linear-to-r from-white to-gray-400 bg-clip-text text-transparent">
                                Escrits
                            </h1>
                            <Button
                                onClick={handleRandomDay}
                                className="bg-linear-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white border-none shadow-lg hover:shadow-xl font-medium px-6 py-3 h-auto transition-all"
                            >
                                Dia aleatori
                            </Button>
                        </div>
                        <p className="text-gray-400 text-sm">
                            Total: <span className="text-red-400 font-semibold">{totalEscrits}</span> escrits
                        </p>
                    </div>

                    {/* Year grids */}
                    <div className="space-y-8">
                        {years.map(year => {
                            const yearEntries = escritsData.filter(e => e.year === year);
                            return <YearGrid key={year} year={year} entries={yearEntries} />;
                        })}
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
}
